/** Export / import helpers for terminal_flow_overrides (Phase 3). */

export const OVERRIDES_EXPORT_VERSION = 1;

export function buildOverridesExport(overrides, meta = {}) {
  return {
    version: OVERRIDES_EXPORT_VERSION,
    app: 'mysky-leads-tracker',
    exported_at: new Date().toISOString(),
    exported_by: meta.exportedBy || null,
    overrides: overrides && typeof overrides === 'object' ? overrides : {},
  };
}

export function parseOverridesImport(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return { error: 'Invalid JSON file.' };
    }
  }
  if (!data || typeof data !== 'object') return { error: 'Import must be a JSON object.' };
  if (data.version != null && data.version !== OVERRIDES_EXPORT_VERSION) {
    return { error: `Unsupported export version ${data.version}. Expected ${OVERRIDES_EXPORT_VERSION}.` };
  }
  const overrides = data.overrides ?? data;
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return { error: 'Missing "overrides" object in import file.' };
  }
  const cleaned = {};
  for (const [key, val] of Object.entries(overrides)) {
    if (!val || typeof val !== 'object' || Array.isArray(val)) continue;
    const patch = {};
    if (typeof val.ask === 'string' && val.ask.trim()) patch.ask = val.ask.trim();
    if (typeof val.hint === 'string' && val.hint.trim()) patch.hint = val.hint.trim();
    if (typeof val.choices === 'string' && val.choices.trim()) patch.choices = val.choices.trim();
    if (val.hint === '') patch.hint = '';
    if (Object.keys(patch).length) cleaned[key] = patch;
  }
  return { overrides: cleaned, meta: data };
}

export function mergeOverrideMaps(base, incoming, mode = 'merge') {
  if (mode === 'replace') return { ...incoming };
  return { ...base, ...incoming };
}

/** Diff two override maps for import preview. */
export function diffOverrideMaps(base = {}, incoming = {}) {
  const added = [];
  const removed = [];
  const changed = [];
  const keys = new Set([...Object.keys(base), ...Object.keys(incoming)]);
  keys.forEach((key) => {
    const b = base[key];
    const i = incoming[key];
    if (i === undefined && b !== undefined) {
      removed.push({ key, before: b });
    } else if (b === undefined && i !== undefined) {
      added.push({ key, after: i });
    } else if (b !== undefined && i !== undefined && JSON.stringify(b) !== JSON.stringify(i)) {
      changed.push({ key, before: b, after: i });
    }
  });
  return {
    added,
    removed,
    changed,
    stats: {
      added: added.length,
      removed: removed.length,
      changed: changed.length,
      totalAfter: Object.keys(incoming).length,
    },
  };
}

export function formatOverrideDiffHtml(diff, escFn) {
  const esc = escFn || ((s) => String(s));
  const fmtPatch = (patch) => {
    if (!patch || typeof patch !== 'object') return '';
    return Object.entries(patch)
      .filter(([, v]) => v != null && String(v).trim() !== '')
      .map(([k, v]) => `${k}: ${String(v).slice(0, 80)}${String(v).length > 80 ? '…' : ''}`)
      .join(' · ');
  };
  const line = (label, items, fmt) => {
    if (!items.length) return '';
    return `<div style="margin-top:8px"><strong>${esc(label)} (${items.length})</strong><ul style="margin:4px 0 0;padding-left:18px;font-size:11px">${items.map(fmt).join('')}</ul></div>`;
  };
  return [
    line('New overrides', diff.added, (x) => `<li><code>${esc(x.key)}</code>${fmtPatch(x.after) ? `<br><span class="admin-muted">${esc(fmtPatch(x.after))}</span>` : ''}</li>`),
    line('Updated', diff.changed, (x) => `<li><code>${esc(x.key)}</code><br><span class="admin-muted">${esc(fmtPatch(x.before))}</span> → <span>${esc(fmtPatch(x.after))}</span></li>`),
    line('Removed', diff.removed, (x) => `<li><code>${esc(x.key)}</code>${fmtPatch(x.before) ? `<br><span class="admin-muted">${esc(fmtPatch(x.before))}</span>` : ''}</li>`),
  ].filter(Boolean).join('') || '<p class="admin-muted">No changes — import matches current overrides.</p>';
}

export function filterOverridesForFlow(all, flowId) {
  const prefix = `${flowId}.`;
  const next = { ...all };
  for (const key of Object.keys(next)) {
    if (key.startsWith(prefix)) delete next[key];
  }
  return next;
}

export function countOverridesForFlow(all, flowId) {
  const prefix = `${flowId}.`;
  return Object.keys(all).filter((k) => k.startsWith(prefix)).length;
}

export function downloadOverridesJson(overrides, filename = 'terminal-flow-overrides.json') {
  const blob = new Blob([JSON.stringify(buildOverridesExport(overrides), null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
